import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {AbstractControl, FormArray, FormGroup, ReactiveFormsModule, ValidatorFn, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {
  OphButtonGroupComponent,
  OphButtonGroupOption,
} from 'src/app/shared/design/oph-button-group/oph-button-group.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphInputAnimateComponent} from 'src/app/shared/design/oph-inputs/oph-input-animate/oph-input-animate.component';
import {OphInputOrangeComponent} from 'src/app/shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {ListViewsModule} from 'src/app/shared/list-views/list-views.module';
import {MetricDisplayPipe} from 'src/app/shared/pipes/metric-display.pipe';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {TrackersService} from 'src/app/shared/services/trackers.service';
import {TrackerMetricTypeDisplayPipe} from 'src/app/trackers/shared/tracker-metric-type-display.pipe';
import {MetricNumericType, TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {TrackerDetailMetricSelectComponent} from './select/tracker-detail-metric-select.component';

@Component({
  selector: 'tracker-detail-metric',
  standalone: true,
  imports: [
    OphInputOrangeComponent,
    MatMenuModule,
    TrackerDetailMetricSelectComponent,
    TrackerMetricTypeDisplayPipe,
    ListViewsModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    OphIconModule,
    OphButtonModule,
    MatIconModule,
    PipesModule,
    OphInputAnimateComponent,
    MetricDisplayPipe,
    OphButtonGroupComponent,
    OphXButtonComponent,
  ],
  templateUrl: './tracker-detail-metric.component.html',
  styleUrl: './tracker-detail-metric.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackerDetailMetricComponent implements OnInit, OnDestroy {
  // Inputs using new input() function
  metricForm = input.required<FormGroup>();
  editing = input<boolean>(false);
  trackerDetail = input<boolean>(false);

  // Outputs using new output() function
  menuAction = output<string>();

  private trackersService = inject(TrackersService);
  private cdr = inject(ChangeDetectorRef);

  // Unified cache for all form state preservation
  private stateCache = new Map<string, any>();
  private nameControlSubscription: Subscription | null = null;
  private numericTypeSubscription: Subscription | null = null;
  private minValueSubscription: Subscription | null = null;
  private maxValueSubscription: Subscription | null = null;
  private durationTypeSubscription: Subscription | null = null;

  // State using signals
  infoTooltipOpen = signal(false);
  nameCharacterCount = signal(0);
  durationTypeValue = signal<string>('hrs/min');

  // Computed properties for derived state
  typeControl = computed(() => this.metricForm().get('type') as AbstractControl);
  optionsFormArray = computed(() => this.metricForm().get('options') as FormArray);
  durationTypeDisplay = computed(() => {
    const durationType = this.durationTypeValue();
    const option = this.durationTypeOptions.find(opt => opt.value === durationType);
    return option?.display || 'Hours / Minutes';
  });

  // Type-safe constants
  readonly typeOptions: string[] = ['Number', 'List', 'Yes / No', 'Text', 'Photo', 'Duration'];
  readonly trackerMetricTypes = TrackerMetricTypes;
  readonly numericTypeOptions: OphButtonGroupOption[] = [
    {name: 'basic', display: 'Basic Field'},
    {name: 'stepper', display: '+/- Stepper'},
    {name: 'range', display: 'Range Slider'},
  ];
  readonly dataOptions: OphButtonGroupOption[] = [
    {name: 'string', display: 'Text'},
    {name: 'number', display: 'Numeric'},
  ];

  readonly booleanOptions: OphButtonGroupOption[] = [
    {name: '1', display: 'Yes'},
    {name: null, display: 'Unset'},
    {name: '0', display: 'No'},
  ];

  readonly durationTypeOptions: {display: string; value: 'hrs' | 'hrs/min' | 'min' | 'min/sec'}[] = [
    {display: 'Hours', value: 'hrs'},
    {display: 'Hrs : Min', value: 'hrs/min'},
    {display: 'Minutes', value: 'min'},
    {display: 'Min : Sec', value: 'min/sec'},
  ];

  readonly durationTypeDisplayOptions: string[] = ['Hours', 'Hrs : Min', 'Minutes', 'Min : Sec'];

  ngOnInit() {
    const nameControl = this.metricForm().get('name');
    if (nameControl) {
      this.nameCharacterCount.set(nameControl.value?.length || 0);

      this.nameControlSubscription = nameControl.valueChanges.subscribe((value: string) => {
        this.nameCharacterCount.set(value?.length || 0);
        this.cdr.markForCheck();
      });
    }

    // Set up min/max validation based on numericType
    const numericTypeControl = this.metricForm().get('numericType');
    if (numericTypeControl) {
      // Apply initial validation state
      this.updateMinMaxValidators(numericTypeControl.value);

      this.numericTypeSubscription = numericTypeControl.valueChanges.subscribe((numericType: string) => {
        this.updateMinMaxValidators(numericType);
        this.cdr.markForCheck();
      });
    }

    // Initialize and subscribe to durationType changes
    const durationTypeControl = this.metricForm().get('durationType');
    if (durationTypeControl) {
      this.durationTypeValue.set(durationTypeControl.value || 'hrs/min');

      this.durationTypeSubscription = durationTypeControl.valueChanges.subscribe((durationType: string) => {
        this.durationTypeValue.set(durationType);
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.nameControlSubscription?.unsubscribe();
    this.numericTypeSubscription?.unsubscribe();
    this.minValueSubscription?.unsubscribe();
    this.maxValueSubscription?.unsubscribe();
    this.durationTypeSubscription?.unsubscribe();
  }

  private updateMinMaxValidators(numericType: string): void {
    const minControl = this.metricForm().get('min');
    const maxControl = this.metricForm().get('max');

    if (!minControl || !maxControl) return;

    // Clean up existing subscriptions
    this.minValueSubscription?.unsubscribe();
    this.maxValueSubscription?.unsubscribe();

    if (this.isMinMaxRequired(numericType)) {
      this.setMinMaxValidators(minControl, maxControl);
      this.subscribeToMinMaxChanges(minControl, maxControl);
    } else {
      this.clearValidators(minControl);
      this.clearValidators(maxControl);
    }
  }

  private isMinMaxRequired(numericType: string): boolean {
    return numericType === MetricNumericType.Stepper || numericType === MetricNumericType.Range;
  }

  private setMinMaxValidators(minControl: AbstractControl, maxControl: AbstractControl): void {
    minControl.setValidators([Validators.required, this.minValidator(maxControl)]);
    maxControl.setValidators([Validators.required, this.maxValidator(minControl)]);
    minControl.updateValueAndValidity();
    maxControl.updateValueAndValidity();
  }

  private subscribeToMinMaxChanges(minControl: AbstractControl, maxControl: AbstractControl): void {
    this.minValueSubscription = minControl.valueChanges.subscribe(() => {
      maxControl.updateValueAndValidity({emitEvent: false});
    });

    this.maxValueSubscription = maxControl.valueChanges.subscribe(() => {
      minControl.updateValueAndValidity({emitEvent: false});
    });
  }

  private minValidator(maxControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl) => {
      const minValue = control.value;
      const maxValue = maxControl.value;

      if (this.hasValue(minValue) && this.hasValue(maxValue) && Number(minValue) > Number(maxValue)) {
        return {minGreaterThanMax: true};
      }
      return null;
    };
  }

  private maxValidator(minControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl) => {
      const maxValue = control.value;
      const minValue = minControl.value;

      if (this.hasValue(minValue) && this.hasValue(maxValue) && Number(maxValue) < Number(minValue)) {
        return {maxLessThanMin: true};
      }
      return null;
    };
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private clearValidators(control: AbstractControl): void {
    control.clearValidators();
    control.updateValueAndValidity();
  }

  onMinBlur() {
    this.validateMinMax('min');
  }

  onMaxBlur() {
    this.validateMinMax('max');
  }

  private validateMinMax(changedField: 'min' | 'max'): void {
    const minControl = this.metricForm().get('min');
    const maxControl = this.metricForm().get('max');

    if (!minControl || !maxControl) return;

    const minValue = minControl.value;
    const maxValue = maxControl.value;

    // Only proceed if both values exist and are not empty
    if (minValue === null || minValue === '' || maxValue === null || maxValue === '') {
      return;
    }

    const min = Number(minValue);
    const max = Number(maxValue);

    if (changedField === 'min' && min > max) {
      maxControl.setValue(min + 1);
    } else if (changedField === 'max' && max < min) {
      minControl.setValue(max - 1);
    }
  }

  private captureFormState(): any {
    return {
      ...this.metricForm().getRawValue(),
      options: [...this.optionsFormArray().value],
    };
  }

  private restoreFormState(state: any, type?: string): void {
    if (!state) return;

    const {options, ...formState} = state;
    this.metricForm().patchValue(formState);

    // Restore options for select type
    if (type === 'select' && options) {
      const optionsArray = this.optionsFormArray();
      while (optionsArray.length) optionsArray.removeAt(0);
      options.forEach((option: any) => {
        optionsArray.push(this.trackersService.getOptionsFormGroup(option));
      });
    }
  }

  private setTypeDefaults(type: string): void {
    const defaults: Record<string, any> = {
      select: {value: '', defaultValue: '', valueType: 'string'},
      boolean: {value: null, defaultValue: null, valueType: 'string'},
      numeric: {value: '0', defaultValue: '0', numericType: 'basic', valueType: 'number'},
      text: {value: '', defaultValue: '', valueType: 'string'},
      photo: {value: '', defaultValue: '', valueType: 'string'},
      duration: {value: '', defaultValue: '', valueType: 'number', durationType: 'hrs/min'},
    };

    this.metricForm().patchValue(defaults[type] || {});
  }

  onTypeChange(displayType: string): void {
    const newType = this.getType(displayType);
    const existingValueType = this.metricForm().get('valueType')?.value;
    const valueType = this.trackersService.getValueTypeForMetricType(newType as TrackerMetricTypes, existingValueType);
    const currentType = this.metricForm().get('type')?.value;

    // Cache current state and switch type
    if (currentType) this.stateCache.set(currentType, this.captureFormState());
    this.metricForm().patchValue({type: newType, valueType});

    // Restore cached state or set defaults
    const cached = this.stateCache.get(newType);
    if (cached) {
      this.restoreFormState(cached, newType);
      if (newType === 'boolean') this.metricForm().patchValue({value: null});
    } else {
      this.setTypeDefaults(newType);
    }
  }

  getType(type: string): string {
    switch (type) {
      case 'Number':
        return 'numeric';
      case 'List':
        return 'select';
      case 'Yes / No':
        return 'boolean';
      case 'Text':
        return 'text';
      case 'Photo':
        return 'photo';
      case 'Duration':
        return 'duration';
      default:
        return 'text';
    }
  }

  onToggle(type: 'value' | 'defaultValue', checked: boolean): void {
    this.metricForm()
      .get(type)
      ?.setValue(checked ? '1' : '0');
  }

  onSelectValueChange(value: string): void {
    this.metricForm().get('value')?.setValue(value);
  }

  onSelectDefaultValueChange(optionIndex: number, checked: boolean): void {
    const options = this.optionsFormArray().controls;
    options.forEach((formGroup: FormGroup, index: number) => {
      formGroup.patchValue({default: index === optionIndex ? checked : false});
    });
  }

  onRemoveOption(index: number): void {
    this.optionsFormArray().removeAt(index);
  }

  onNewOption(): void {
    // Get last option's value and increment by 1 for sequential numbering
    const optionsArray = this.optionsFormArray();
    const lastValue =
      optionsArray.length > 0 ? Number(optionsArray.at(optionsArray.length - 1).get('value')?.value) || 0 : 0;

    optionsArray.push(this.trackersService.getOptionsFormGroup({value: lastValue + 1, option: ''}));
  }

  onNumericTypeChange(numericType: string): void {
    const currentType = this.metricForm().get('type')?.value;
    const currentNumericType = this.metricForm().get('numericType')?.value;
    const cacheKey = `${currentNumericType}_${currentType}`;
    const newCacheKey = `${numericType}_${currentType}`;

    // Cache and restore
    if (currentNumericType) this.stateCache.set(cacheKey, this.captureFormState());
    this.metricForm().patchValue({numericType});

    const cached = this.stateCache.get(newCacheKey);
    if (cached) this.restoreFormState(cached);

    // Update min/max validators based on the new numeric type
    this.updateMinMaxValidators(numericType);
  }

  onDataChange(data: string): void {
    const currentType = this.metricForm().get('valueType')?.value;
    const cacheKey = `valueType_${currentType}`;
    const newCacheKey = `valueType_${data}`;

    // Cache current options and restore
    if (currentType) this.stateCache.set(cacheKey, [...this.optionsFormArray().value]);
    this.metricForm().patchValue({valueType: data});

    const cached = this.stateCache.get(newCacheKey);
    const defaultOption = data === 'string' ? {option: '', value: ''} : {option: '', value: 1};
    const optionsToLoad = cached || [defaultOption];

    const optionsArray = this.optionsFormArray();
    while (optionsArray.length) optionsArray.removeAt(0);
    optionsToLoad.forEach((option: any) => {
      optionsArray.push(this.trackersService.getOptionsFormGroup(option));
    });
  }

  getBooleanSelectedValue(): string | null {
    const value = this.metricForm().get('value')?.value;
    if (value === '1') return '1';
    if (value === '0') return '0';
    return null;
  }

  toggleInfoTooltip(): void {
    this.infoTooltipOpen.update(isOpen => !isOpen);
  }

  closeInfoTooltip(): void {
    this.infoTooltipOpen.set(false);
  }

  onOptionValueChange(value: string, index: number): void {
    // For numeric type, sync the option input to the value input
    if (this.metricForm().get('valueType')?.value !== 'string') {
      const optionsArray = this.optionsFormArray();
      const formGroup = optionsArray.at(index) as FormGroup;
      formGroup.get('value')?.setValue(value);
    }
  }

  onDurationTypeDisplayChange(displayValue: string): void {
    const option = this.durationTypeOptions.find(opt => opt.display === displayValue);
    if (option) {
      const durationTypeControl = this.metricForm().get('durationType');
      if (durationTypeControl) {
        durationTypeControl.setValue(option.value);
        this.durationTypeValue.set(option.value);
        this.cdr.markForCheck();
      }
    }
  }
}
